import { useNavigate } from 'react-router-dom';
import { Stack, Text } from '@repletoryreach/sdk-ui';
import { DataTable } from '@repletoryreach/sdk-ui/data-display';
import { notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import type { admin } from '@repletoryreach/sdk-api';
import type { Indexed } from '../../../lib/types';
import { formatDateTime, formatRelative } from '../../../lib/format';
import { TenantName } from '../../../components/TenantName';
import { TwoLineCell } from '../../../components/TwoLineCell';
import { CopyableId } from '../../../components/CopyableId';
import { RunStatusBadge } from './RunStatusBadge';
import { RowMenu } from '../../../components/RowMenu';
import { formatDuration } from '../lib/duration';
import { useT } from '../../../i18n/I18nProvider';

type FlowRun = NonNullable<Awaited<ReturnType<typeof admin.listFlowRuns>>['data']>[number];

export function FlowsTable({ records, loading }: { records: FlowRun[]; loading: boolean }) {
  const navigate = useNavigate();
  const t = useT();

  async function copy(value: string) {
    try {
      await navigator.clipboard.writeText(value);
      notifySuccess(value, t('common.copied'));
    } catch {
      // A denied clipboard is the browser's decision to report, not ours.
    }
  }

  return (
    <DataTable<Indexed<FlowRun>>
      records={records as Indexed<FlowRun>[]}
      loading={loading}
      idAccessor="id"
      emptyState={t('flows.table.empty')}
      onRowClick={(row) => navigate(`/flows/${row.id}`)}
      columns={[
        {
          accessor: 'flow_name',
          title: t('flows.columns.flow'),
          sortable: true,
          // The channel rides under the name rather than taking a column of
          // its own: it qualifies the run, it is never scanned on its own.
          // A deleted flow has no name left, so the id becomes the only way to
          // tell two of them apart and is worth copying.
          render: (r) => (r.flow_name
            ? <TwoLineCell primary={r.flow_name} secondary={t('flows.cell.channel', { channel: r.channel })} title={r.flow_name} />
            : <CopyableId value={r.flow_id} label={t('flows.copyFlowId')} />)
        },
        {
          accessor: 'organisation_id',
          title: t('flows.columns.tenant'),
          render: (r) => <TenantName id={r.organisation_id} />
        },
        {
          accessor: 'contact_msisdn',
          title: t('flows.columns.contact'),
          render: (r) => r.contact_msisdn ?? <Text role="caption" c="var(--rr-color-text-muted)">{t('common.notAvailable')}</Text>
        },
        {
          accessor: 'status',
          title: t('common.status'),
          render: (r) => (
            <Stack gap={2} style={{ minWidth: 0 }}>
              <RunStatusBadge status={r.status} />
              {r.error && (
                <span title={r.error}>
                  <Text role="caption" c="var(--rr-color-error)" lineClamp={1}>{r.error}</Text>
                </span>
              )}
            </Stack>
          )
        },
        {
          accessor: 'finished_at',
          title: t('flows.columns.duration'),
          render: (r) => (
            <span title={r.finished_at ? formatDateTime(r.finished_at) : t('flows.view.overview.stillRunning')}>
              {formatDuration(r.started_at, r.finished_at)}
            </span>
          )
        },
        {
          accessor: 'started_at',
          title: t('flows.columns.startedAt'),
          sortable: true,
          render: (r) => <TwoLineCell primary={formatDateTime(r.started_at)} secondary={formatRelative(r.started_at)} />
        },
        {
          accessor: 'actions',
          title: t('common.actions'),
          width: 64,
          textAlign: 'right',
          render: (r) => (
            <RowMenu
              actions={[
                { id: 'view', label: t('flows.actions.view'), onSelect: () => navigate(`/flows/${r.id}`) },
                { id: 'tenant', label: t('flows.actions.openTenant'), onSelect: () => navigate(`/tenants/${r.organisation_id}`) },
                { id: 'run', label: t('flows.copyRunId'), onSelect: () => void copy(r.id) },
                ...(r.contact_msisdn
                  ? [{ id: 'contact', label: t('flows.actions.copyContact'), onSelect: () => void copy(r.contact_msisdn as string) }]
                  : [])
              ]}
            />
          )
        }
      ]}
    />
  );
}
